import { Box, TextFieldProps, SxProps } from '@mui/material';
import { Input, Props as InputProps } from 'components/Inputs/Input';
import { ReactElement } from 'react';
import { EditableTextInput } from 'components/Inputs/EditableTextInput';
import { SectionHeader } from './SectionHeader';

const containerStyle: SxProps = {
  display: 'flex'
};

type TextFieldConfig = {
  label: string;
  props: InputProps;
  rightComponent?: ReactElement | null;
  withRemoveButton?: boolean;
  onRemove?: () => void;
  onSave?: (value: string) => void;
  maxCharacters?: number;
  inputContainerStyle?: SxProps;
  isEditableText?: boolean;
  disableMargin?: boolean;
};

const TextField: React.FC<{
  field: TextFieldConfig;
  index: number;
}> = ({ field, index }) => {
  return (
    <Box
      key={`${field.label}-${index}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: field.disableMargin ? 0 : '24px',
        ...field.inputContainerStyle
      }}
    >
      <Box sx={{ width: '100%' }} key={`text-field-${index}`}>
        {field.isEditableText ? (
          <EditableTextInput
            label={field.label}
            onSave={(value) => field.onSave?.(value)}
            {...field.props}
          />
        ) : (
          <Input
            label={field.label}
            {...field.props}
            maxCharacters={field.maxCharacters}
            withRemoveButton={field.withRemoveButton}
            onRemove={field.onRemove}
          />
        )}
      </Box>
      {field?.rightComponent ? field.rightComponent : null}
    </Box>
  );
};

type CustomTextFieldProps = TextFieldProps & {
  title?: string;
  titleSx?: SxProps;
  subTitle?: string | ReactElement;
  textFields?: TextFieldConfig[];
  customComponents?: (ReactElement | null)[];
  width?: number | string;
  headerId?: string;
  disableMargin?: boolean;
  FieldsContainer?: React.FC<unknown>;
  isVertical?: boolean;
  gap?: string;
};

/**
 * @deprecated use common/form/FormField instead
 */
export const FormField: React.FC<CustomTextFieldProps> = ({
  title,
  titleSx,
  subTitle,
  textFields,
  customComponents,
  disableMargin,
  headerId,
  width = '50%',
  gap = '80px',
  FieldsContainer,
  isVertical
}) => {
  const Wrapper = FieldsContainer || Box;

  return (
    <Box
      id={headerId}
      data-testid={headerId}
      sx={{
        ...containerStyle,
        marginBottom: disableMargin ? 0 : '24px',
        flexDirection: isVertical ? 'column' : 'row',
        gap
      }}
    >
      <Box width={381}>
        {title && (
          <SectionHeader title={title} titleSx={titleSx} subTitle={subTitle} />
        )}
      </Box>
      <Box sx={{ width }}>
        <Wrapper>
          {textFields?.map((field, index) => (
            <TextField
              key={`text-fields-${index}`}
              field={field}
              index={index}
            />
          ))}
          <Box mt={isVertical ? 2 : 0}>
            {customComponents?.map((component, index) =>
              component ? (
                <Box key={`component-${index}`} mb={4}>
                  {component}
                </Box>
              ) : null
            )}
          </Box>
        </Wrapper>
      </Box>
    </Box>
  );
};
